import ImageCard from '@components/common/ImageCard';
import { DATA_BASE_URL } from '@lib/constants';

const teamMembers = [
  {
    id: 1,
    role: 'Team Member',
    image: `${DATA_BASE_URL}/data/badminton/photos/varsity_badminton.webp`,
    description: 'Varsity badminton team demonstrating strong coordination and team spirit',
  },
  {
    id: 2,
    role: 'IASAS Representative',
    image: `${DATA_BASE_URL}/data/badminton/photos/iasas_singapore_25_badminton_team_ism.webp`,
    description: 'ISM team at IASAS Singapore 2025 international tournament',
  },
];

const teamValues = [
  {
    id: 1,
    title: 'Teamwork',
    description: 'Collaborating with teammates to achieve collective success on and off the court',
    icon: (
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
      </svg>
    ),
  },
  {
    id: 2,
    title: 'Agility',
    description: 'Quick reflexes and fast-paced movement essential for competitive badminton',
    icon: (
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
  {
    id: 3,
    title: 'Sportsmanship',
    description: 'Competing with integrity, respect, and fair play in every match. Received Sportsmanship Award.',
    icon: (
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
  {
    id: 4,
    title: 'Dedication',
    description: 'Committed training and continuous improvement to excel in competitive badminton. Received Most Improved Player Award.',
    icon: (
      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ),
  },
];

const TeamShowcaseSection = () => {
  return (
    <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-theme-2)' }}>
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 space-y-4">
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold tracking-tight"
            style={{ color: 'var(--color-heading)' }}
          >
            Team Excellence
          </h2>
          <p
            className="text-base sm:text-lg max-w-3xl mx-auto"
            style={{ color: 'var(--color-subtle)' }}
          >
            Representing ISM with pride, teamwork, and competitive spirit
          </p>
        </div>

        {/* Team Photos Grid - Mobile: horizontal scroll, Desktop: grid */}
        <div className="md:grid md:grid-cols-2 md:gap-8 flex md:flex-none overflow-x-auto gap-6 pb-6 snap-x snap-mandatory scrollbar-hide mb-12 sm:mb-16 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-0 lg:px-0">
          {teamMembers.map((member) => (
            <div key={member.id} className="flex-shrink-0 w-[85vw] sm:w-[75vw] md:w-auto snap-start space-y-3 sm:space-y-4">
              <ImageCard
                src={member.image}
                alt={member.role}
                aspectRatio="aspect-video"
                className="hover:scale-105 transition-transform duration-300"
              />
              <div className="text-center space-y-2">
                <h3
                  className="text-lg sm:text-xl font-semibold"
                  style={{ color: 'var(--color-heading)' }}
                >
                  {member.role}
                </h3>
                <p
                  className="text-xs sm:text-sm"
                  style={{ color: 'var(--color-muted)' }}
                >
                  {member.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile scroll hint */}
        <div className="md:hidden text-center mb-12">
          <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
            Swipe to see more team photos
          </p>
        </div>

        {/* Team Values - Mobile: horizontal scroll, Desktop: grid */}
        <div className="lg:grid lg:grid-cols-4 lg:gap-8 flex lg:flex-none overflow-x-auto gap-6 pb-6 snap-x snap-mandatory scrollbar-hide -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-0 lg:px-0">
          {teamValues.map((value) => (
            <div
              key={value.id}
              className="flex-shrink-0 w-[75vw] sm:w-[60vw] lg:w-auto snap-start p-5 sm:p-6 rounded-xl shadow-card transition-all duration-300 hover:shadow-card-hover"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                borderColor: 'var(--color-card-border)',
                borderWidth: '1px',
                borderStyle: 'solid',
              }}
            >
              <div className="space-y-3 sm:space-y-4">
                {/* Icon */}
                <div
                  className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-lg"
                  style={{ backgroundColor: 'var(--color-accent-2)', color: 'var(--color-accent-7)' }}
                >
                  {value.icon}
                </div>

                {/* Title */}
                <h3
                  className="text-lg sm:text-xl font-semibold"
                  style={{ color: 'var(--color-heading)' }}
                >
                  {value.title}
                </h3>

                {/* Description */}
                <p
                  className="text-xs sm:text-sm leading-relaxed"
                  style={{ color: 'var(--color-muted)' }}
                >
                  {value.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile scroll hint */}
        <div className="lg:hidden text-center mt-6">
          <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
            Swipe to see more team values
          </p>
        </div>
      </div>
    </section>
  );
};

export default TeamShowcaseSection;
